import React from 'react';
import { useNavigate } from 'react-router-dom';
import Reveal from './Reveal';
import OptimizedImage from './OptimizedImage';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const timeline = [
    {
      year: '1973年',
      title: '東京、世田谷生まれ',
      description:
        '3歳のときに世田谷区三軒茶屋から町田市玉川学園に引越し。玉川中央幼稚園、町田第5小学校、その後、本町田への転居に伴い、町田第3小学校に転校。桐蔭学園中学・高校卒業、東京女子大学文理学部社会学科卒業。中学3年生のときに父を肺がんで亡くし、以降母ひとり子ひとりのシングル家庭で育つ。',
      image: '/1973.jpg'
    },
    {
      year: '1996年',
      title: '編集者・ライターになる',
      description:
        '大学卒業後、出版社の（株）扶桑社に入社。書店営業部を経てESSE編集部に異動。その後、結婚・出産を機にフリーランスライターとして独立し、生活情報誌と生活実用書中心の執筆活動を行う。インタビュー記事の執筆も精力的にこなす。',
      image: '/1996.jpg'
    },
    {
      year: '2015年',
      title: '「玉川つばめ通信」創刊',
      description:
        '息子ふたりの小学校のPTA会長になったことがきっかけで地域とのつながりが生まれ、玉川学園のひとりメディア「玉川つばめ通信」を立ち上げる。さらに「スナックつばめ」「小料理つばめ」「ゆるつばめ会議」もスタート。これまで玉川学園、町田を中心に1000人以上の方を取材した。2025年9月、創刊10周年の節目に町田パリオでの「パリコレッ！芸術祭3week」に参加。1〜36号＋臨時増刊号等、バックナンバーを一挙展示した。',
      image: '/2015.jpg'
    },
    {
      year: '2018年',
      title: '「玉川音頭」を復活',
      description:
        '玉川学園の駅前広場で1946年にたった1度行われた「玉川音頭」を玉川学園ポケットパークで72年ぶりに復活させる。以降、玉川音頭はまちのあちこちで踊り継がれるようになった。',
      image: '/2018.jpg'
    },
    {
      year: '2021年',
      title: '町田市の再開発問題に関心を持つ',
      description:
        '子ども時代の大切な居場所だった「町田市立博物館」の閉館がきっかけで、町田市の再開発計画に関心・疑問を持つ。同年10月発足の「芹ヶ谷公園をより良くする市民の会」共同代表になる。',
      image: '/2021.jpg'
    },
    {
      year: '2025年',
      title: '政治活動スタート',
      description:
        '子どもの不登校、夫の難病（重症筋無力症）、自身の乳がんを経験し、人生の様々な局面でたくさんの人に支えられたことを実感する。乳がん治療が終わり、子育ても一段落した今、支えてもらった恩返しとして地域に貢献したいと市政へのチャレンジを決意する。',
      image: '/2025.jpg'
    }
  ];

  return (
    <section id="profile" className="section bg-gray-50">
      <div className="container">
        <Reveal className="text-center mb-12" delay={0.05}>
          <h2 className="section-title">
            プロフィール
          </h2>
        </Reveal>

        <div className="max-w-4xl mx-auto">
          {/* Timeline（塊で同時に表示） */}
          <Reveal className="card" delay={0}>
            <h3 className="text-xl font-bold mb-8 text-gray-800 border-b border-pink-200 pb-2" style={{ borderBottomColor: '#e3628d' }}>
              経歴
            </h3>
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <Reveal key={item.year} delay={0.08 * index}>
                  <div className={index > 0 ? 'border-t border-gray-200 mt-6 pt-6' : ''}>
                    <div className="flex items-center space-x-4 md:space-x-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#e3628d' }}>
                          {item.year}
                        </div>
                      </div>
                      <h4 className="text-xl font-bold text-gray-800">
                        {item.title}
                      </h4>
                    </div>
                    <p className="mt-2 text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                    {item.image && (
                      <div className="mt-4 w-full flex justify-center">
                        <OptimizedImage
                          src={item.image}
                          alt={`${item.year}の写真`}
                          className="w-[640px] max-w-full h-auto rounded-md shadow-sm mx-auto"
                          widths={[480, 640, 768, 1024]}
                          sizes="(max-width: 700px) 100vw, 640px"
                        />
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>

          {/* My Thoughts */}
          <Reveal className="card mt-8" delay={0.2}>
            <h3 className="text-xl font-bold mb-6 text-gray-800 border-b border-pink-200 pb-2" style={{ borderBottomColor: '#e3628d' }}>
              私の想い
            </h3>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                <span style={{ color: '#e3628d', fontWeight: 'bold' }}>「無所属。右でも左でもなく真ん中」。</span>この言葉には政党の理念に左右されず、 忖度しないで市民一人ひとりの声に耳を傾け、心の通った解決策を見つけたい、という想いを込めています。
              </p>
              <p>
                子育てや乳がんを経験し、人生の様々な局面で地域の支え、人とのつながりを感じてきました。
                今こそ、その恩返しとして地域に貢献したいと、新たな挑戦を決意しました。
              </p>
              <p>
                町田をより強く、やさしく、しなやかなまちにするために、対話の力を信じて粘り強く活動を続けます。
              </p>
              <div className="mt-2 w-full flex justify-center">
                <OptimizedImage
                  src="/eki_unotsu.jpg"
                  alt="駅前での活動の様子"
                  className="w-[640px] max-w-full h-auto rounded-md shadow-sm mx-auto"
                  widths={[480, 640, 768, 1024]}
                  sizes="(max-width: 700px) 100vw, 640px"
                />
              </div>
            </div>
          </Reveal>

        </div>

        {/* Call to Action */}
        <Reveal className="text-center mt-12" delay={0.3}>
          <button
            className="btn text-base px-6 py-3"
            onClick={() => {
              navigate('/profile-detail');
            }}
          >
            プロフィール詳細
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default Profile; 